import React, { useEffect } from 'react';
import { Combobox, useListCollection } from '@ark-ui/react/combobox';
import { Portal } from '../../../components/Portal';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

const POPOVER_POSITIONING = {
  sameWidth: true,
  placement: 'bottom',
  gutter: 4,
} as const;

export interface ComboboxOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface ComboboxInputProps extends BaseInputProps<string> {
  options: ComboboxOption[];
  /** Text při prázdném výsledku filtru. */
  emptyText?: string;
  /** Lokalizovaný popisek mazacího tlačítka. */
  clearLabel?: string;
  /** Lokalizovaný popisek tlačítka pro otevření nabídky. */
  triggerLabel?: string;
  className?: string;
}

/**
 * Atomický combobox (select s fulltextovým filtrováním) postavený na Ark UI
 * `Combobox`.
 *
 * RHF drží jednu `string` hodnotu vybrané položky (Ark interně pole hodnot).
 * Filtrování řeší Ark `useListCollection` (mutace kolekce mimo render —
 * přestavba kolekce při každém úhozu by rozhazovala controlled input).
 * `ids.input` mapuje `id` z `<FormField />`, `ids.label` pojmenovává widget.
 */
export const ComboboxInput: React.FC<ComboboxInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  labelId,
  testAttributes,
  options,
  emptyText = 'Nic nenalezeno',
  clearLabel = 'Vymazat výběr',
  triggerLabel = 'Otevřít nabídku',
  className,
}) => {
  const { collection, filter, set } = useListCollection<ComboboxOption>({
    initialItems: options,
    filter: (itemText, filterText) =>
      itemText.toLowerCase().includes(filterText.toLowerCase()),
  });

  // Externí změna možností (async načtení) se musí do kolekce propsat.
  useEffect(() => {
    set(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const ids = React.useMemo(
    () => ({ input: id, label: labelId }),
    [id, labelId]
  );

  const selectedValue = React.useMemo(
    () => (value ? [value] : []),
    [value]
  );

  return (
    <Combobox.Root
      ids={ids}
      collection={collection}
      value={selectedValue}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      onValueChange={(details) => {
        onChange(details.value[0] ?? '');
        onBlur();
      }}
      onInputValueChange={(details) => filter(details.inputValue)}
      onInteractOutside={onBlur}
      positioning={POPOVER_POSITIONING}
      lazyMount
      unmountOnExit
      className={clsx(className)}
    >
      <Combobox.Control className={styles.comboboxControl}>
        <Combobox.Input
          ref={ref}
          name={name}
          placeholder={placeholder}
          className={styles.comboboxInput}
          aria-describedby={describedBy}
          {...testAttributes}
        />
        <div className={styles.comboboxIndicators}>
          <Combobox.ClearTrigger
            className={styles.comboboxClear}
            aria-label={clearLabel}
          >
            <Icon name="x-mark" size={16} />
          </Combobox.ClearTrigger>
          <Combobox.Trigger
            className={styles.comboboxTrigger}
            aria-label={triggerLabel}
          >
            <Icon name="chevron-down" size={18} />
          </Combobox.Trigger>
        </div>
      </Combobox.Control>
      <Portal>
        <Combobox.Positioner>
          <Combobox.Content
            className={styles.content}
            data-testid={`${testAttributes['data-testid']}-content`}
          >
            <Combobox.Empty className={styles.item}>{emptyText}</Combobox.Empty>
            {collection.items.map((option) => (
              <Combobox.Item
                key={option.value}
                item={option}
                className={styles.item}
                data-testid={`${testAttributes['data-testid']}-option-${option.value}`}
              >
                <Combobox.ItemText>{option.label}</Combobox.ItemText>
                <Combobox.ItemIndicator className={styles.itemIndicator}>
                  <Icon name="check" size={16} />
                </Combobox.ItemIndicator>
              </Combobox.Item>
            ))}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};

ComboboxInput.displayName = 'ComboboxInput';
